import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { type EfficiencyUnit, isEfficiencyUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnits, formatUnitsAbbr } from './units'

const efficiencyFormats: UnitsFormat<EfficiencyUnit> = {
  'km/kwh': {
    long: 'Kilometers per Kilowatt Hour',
    short: 'km/kWh'
  },
  'kwh/km': {
    long: 'Kilowatt Hours per Kilometer',
    short: 'kWh/km'
  },
  'mi/kwh': {
    long: 'Miles per per Kilowatt Hour',
    short: 'mi/kWh'
  },
  'kwh/mi': {
    long: 'Kilowatt Hours per Mile',
    short: 'kWh/mi'
  }
}

export const formatEfficiencyUnits = formatUnits(efficiencyFormats)
export const formatEfficiencyUnitsAbbr = formatUnitsAbbr(efficiencyFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'kWh/mi',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useEfficiencyFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: efficiency,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      const efficiencyUnit = units.toLowerCase()
      if (!isEfficiencyUnit(efficiencyUnit)) return invalidStr
      if (typeof efficiency !== 'string' && typeof efficiency !== 'number') return invalidStr

      // Coerce to number
      const efficiencyNum = parseFloat(`${efficiency}`)
      if (isNaN(efficiencyNum) || efficiencyNum < 0) return invalidStr

      const efficiencyStr = formatNumber(efficiencyNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return efficiencyStr

      const formattedUnits = longForm
        ? formatEfficiencyUnits(efficiencyUnit)
        : formatEfficiencyUnitsAbbr(efficiencyUnit)
      return [efficiencyStr, formattedUnits].join(' ')
    },
    [formatNumber]
  )
}
